import { useCallback, useContext, useEffect, useState } from 'react';
import { Button } from 'antd';
import { DownCircleOutlined } from '@ant-design/icons';
import { OfferTypeEnum } from 'enums/offerType';
import { Order } from 'model/Order';
import { useForm } from 'antd/lib/form/Form';
import { OrdersFiltersParam } from 'model/types';
import { DateTypeFilterEnum } from 'enums/dateTypeFilter';
import { SaleTypeFilterEnum } from 'enums/saleTypeFilter';
import { fetchMyOrders } from 'services/ordersService';
import { AxiosResponse } from 'axios';
import Response from 'model/Response';
import { Loader, Overlay } from 'components/Wired/DefaultLoader/styles';
import FormFiltersFields from 'model/FormFiltersFields';
import { myOrdersContext } from 'contexts/MyOrdersContext';
import {
  isCardsLayout,
  useResponsiveLayout,
} from 'hooks/layout/useResponsiveLayout';
import MyOrdersTable from '../MyOrdersTable';
import { PaginationContainer, ResponsiveContent } from '../styles';
import MyOrdersFilter from '../MyOrdersFilter';
import MyOrdersCardList from '../CardList/MyOrdersCardList';
import MyOrdersResponsiveHeader from '../Header/MyOrdersResponsiveHeader';
import { MyOrderDetailPanel } from '../Detail/MyOrderDetail';

interface Props {
  customerId: number;
}

const PAGE_SIZE = 10;

export default function Tab({ customerId }: Props) {
  const [showPagination, setShowPagination] = useState<boolean>(false);
  const [dataTable, setDataTable] = useState<Order[] | undefined>([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [totalResults, setTotalResults] = useState<number>(0);
  const [appliedValues, setAppliedValues] =
    useState<Partial<FormFiltersFields>>();
  const layout = useResponsiveLayout();
  const [isFilterSheetOpen, setIsFilterSheetOpen] = useState(
    layout === 'phone',
  );
  const [detailedOrder, setDetailedOrder] = useState<Order | null>(null);
  const [form] = useForm();
  const { loading, setLoading } = useContext(myOrdersContext);
  const showsCards = isCardsLayout(layout);
  const getSaleTypeParameters = useCallback(
    (saleType: string, filters: OrdersFiltersParam) => {
      if (saleType === SaleTypeFilterEnum.DIRECTED) {
        filters['Data.IsDirected'] = true;
      }
      if (saleType === SaleTypeFilterEnum.VOL) {
        filters['Data.IsVol'] = true;
      }
      if (saleType === SaleTypeFilterEnum.LKP) {
        filters['Data.IsTransit'] = true;
        filters['Data.IsReceived'] = true;
      }
      if (saleType === SaleTypeFilterEnum.ALL) {
        filters['Data.IsDirected'] = true;
        filters['Data.IsVol'] = true;
        filters['Data.IsTransit'] = true;
        filters['Data.IsReceived'] = true;
      }
    },
    [],
  );
  const handleFilters = useCallback(
    (formFiltersFields: FormFiltersFields) => {
      const filters: OrdersFiltersParam = {
        'Data.Status': formFiltersFields['Data.Status'],
        'Data.SiteTradingName': formFiltersFields['Data.SiteTradingName'],
        'Data.ProductDescription': formFiltersFields['Data.ProductDescription'],
        'Data.OrderNumber': formFiltersFields['Data.OrderNumber'],
        'Data.UserName': formFiltersFields['Data.UserName'],
      };
      getSaleTypeParameters(formFiltersFields['Data.SaleTypes'], filters);
      if (formFiltersFields['Data.TypeDate'] === DateTypeFilterEnum.BUY_DATE) {
        filters['Data.SaleDate'] =
          formFiltersFields['Data.Date'].format('YYYY-MM-DD');
      }
      if (
        formFiltersFields['Data.TypeDate'] === DateTypeFilterEnum.DELIVERY_DATE
      ) {
        filters['Data.DeliveryDate'] =
          formFiltersFields['Data.Date'].format('YYYY-MM-DD');
      }
      if (
        formFiltersFields['Data.TypeDate'] === DateTypeFilterEnum.BILLING_DATE
      ) {
        filters['Data.BillingDate'] =
          formFiltersFields['Data.Date'].format('YYYY-MM-DD');
      }
      return filters;
    },
    [getSaleTypeParameters],
  );

  const fetchOrders = useCallback(
    (formFilters: OrdersFiltersParam, pageNumber: number) => {
      setLoading(true);
      fetchMyOrders({
        'Data.CustomerId': customerId,
        pageNumber,
        pageSize: PAGE_SIZE,
        ...formFilters,
      })
        .then((response: AxiosResponse<Response<Order[]>>) => {
          setLoading(false);
          const { results, totalCount } = response.data;
          setDataTable((oldValue) => {
            if (typeof oldValue !== 'undefined') {
              return [...oldValue, ...results];
            }
            return [...results];
          });
          setTotalResults(totalCount);
        })
        .catch(() => {
          setLoading(false);
        });
    },
    [customerId, setLoading],
  );

  const submit = useCallback(
    (pageNumberParam?: number) => {
      const formFiltersFields: FormFiltersFields = form.getFieldsValue();
      const formFilters = handleFilters(formFiltersFields);
      fetchOrders(formFilters, pageNumberParam ?? 1);
    },
    [form, handleFilters, fetchOrders],
  );

  const onFormSubmit = useCallback(() => {
    setDataTable([]);
    setTotalResults(0);
    setPageNumber(1);
    setAppliedValues(form.getFieldsValue());
    submit();
  }, [form, submit]);

  const isLastPage = useCallback(
    () => typeof dataTable !== 'undefined' && dataTable.length >= totalResults,
    [dataTable, totalResults],
  );

  const onLoadMoreClick = useCallback(() => {
    setPageNumber((oldValue) => oldValue + 1);
    submit(pageNumber + 1);
  }, [submit, pageNumber]);

  useEffect(() => {
    if (isLastPage()) {
      setShowPagination(false);
    } else {
      setShowPagination(true);
    }
  }, [dataTable, isLastPage]);

  useEffect(() => {
    setDataTable([]);
  }, [customerId]);

  const orders = dataTable ?? [];

  return (
    <ResponsiveContent data-cards={showsCards}>
      {showsCards && (
        <MyOrdersResponsiveHeader
          form={form}
          orders={orders}
          totalCount={totalResults}
          appliedValues={appliedValues}
          onOpenFilters={
            layout === 'phone' ? () => setIsFilterSheetOpen(true) : undefined
          }
        />
      )}
      <MyOrdersFilter
        form={form}
        layout={layout}
        appliedValues={appliedValues}
        isSheetOpen={isFilterSheetOpen}
        onCloseSheet={() => setIsFilterSheetOpen(false)}
        onSubmit={onFormSubmit}
      />
      {showsCards ? (
        <MyOrdersCardList
          orders={orders}
          totalCount={totalResults}
          isLoading={loading}
          hasSearched={Boolean(appliedValues)}
          hasMore={showPagination}
          onLoadMore={onLoadMoreClick}
        />
      ) : (
        <>
          <MyOrdersTable
            form={form}
            offerType={OfferTypeEnum.VOL}
            data={dataTable}
            totalCount={totalResults}
            onRowClick={setDetailedOrder}
          />
          {showPagination && (
            <PaginationContainer>
              <Button
                onClick={() => onLoadMoreClick()}
                type="primary"
                icon={<DownCircleOutlined />}
              >
                Carregar mais itens
              </Button>
            </PaginationContainer>
          )}
          <MyOrderDetailPanel
            order={detailedOrder}
            onClose={() => setDetailedOrder(null)}
          />
        </>
      )}
      {loading && !showsCards && (
        <Overlay>
          <Loader spinning={loading} size="large" />
        </Overlay>
      )}
    </ResponsiveContent>
  );
}
